import React, { useEffect, useState, useMemo } from "react";
import { axiosSalary } from "../api/axiosInstance";
import { FaPlus, FaEdit, FaTrash, FaFileExcel } from "react-icons/fa";
import AddEditShiftMaster from "./AddEditShiftMaster"; // import your modal

const ShiftMaster = () => {
  /* ================= SAFE USER ================= */
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {}
  const groupId = storedUser?.groupId;

  /* ================= STATE ================= */
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  /* ================= FETCH SHIFTS ================= */
  const fetchShifts = async () => {
    try {
      setLoading(true);
      const res = await axiosSalary.get(
        `/AttendanceShift/GetByGroup?groupId=${groupId}`
      );
      setShifts(res.data || []);
    } catch (err) {
      console.error("Shift fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  /* ================= HELPERS ================= */
  const shiftTypeLabel = (type) => {
    switch (type) {
      case 1:
        return "Opening";
      case 2:
        return "Reporting";
      case 3:
        return "Closing";
      default:
        return "Unknown";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this shift?")) return;
    try {
      await axiosSalary.delete(`/AttendanceShift/${id}`);
      fetchShifts();
    } catch (err) {
      console.error("Delete error:", err);
      alert("Delete failed");
    }
  };

  const filteredShifts = useMemo(
    () => shifts.sort((a, b) => a.shiftId - b.shiftId),
    [shifts]
  );

  /* ================= EXPORT EXCEL ================= */
  const exportExcel = () => {
    if (!filteredShifts.length) return;

    const rows = filteredShifts.map((s) => ({
      ShiftName: `"${s.shiftName}"`,
      Type: shiftTypeLabel(s.shiftType),
      ReportingTime: s.reportingTime,
      ClosingTime: s.closingTime,
      LateGraceMinutes: s.lateGraceMinutes,
      EarlyLeaveGraceMinutes: s.earlyLeaveGraceMinutes,
      // Status: s.isActive ? "Active" : "Inactive",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [Object.keys(rows[0]).join(","), ...rows.map((e) => Object.values(e).join(","))].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "ShiftMaster.csv";
    link.click();
  };

  /* ================= MODAL HANDLERS ================= */
  const openAddModal = () => {
    setEditingData(null);
    setModalOpen(true);
  };

  const openEditModal = (data) => {
    setEditingData(data);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleModalSuccess = () => {
    fetchShifts();
  };

  /* ================= RENDER ================= */
  return (
    <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 border border-slate-200 min-h-[calc(100vh-120px)]">

      {/* HEADER */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Shift Master</h2>
        <div className="flex gap-2">
          <button
            onClick={exportExcel}
            className="bg-green-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <FaFileExcel /> Excel
          </button>

          <button
            onClick={openAddModal}
            className="bg-blue-600 text-white px-3 py-2 rounded-lg flex items-center gap-2"
          >
            <FaPlus /> Add Shift
          </button>
        </div>
      </div>

      {/* DESKTOP TABLE */}
      <div className="hidden md:block border rounded-lg">
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="p-2 border">Shift Name</th>
                <th className="p-2 border">Type</th>
                <th className="p-2 border">Reporting Time</th>
                <th className="p-2 border">Closing Time</th>
                <th className="p-2 border">Late Grace</th>
                <th className="p-2 border">Early Leave Grace</th>
                {/* <th className="p-2 border">Status</th> */}
                <th className="p-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center p-6">
                    Loading...
                  </td>
                </tr>
              ) : filteredShifts.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50 even:bg-gray-50">
                  <td className="p-2 border">{s.shiftName}</td>
                  <td className="p-2 border">{shiftTypeLabel(s.shiftType)}</td>
                  <td className="p-2 border">{s.reportingTime}</td>
                  <td className="p-2 border">{s.closingTime}</td>
                  <td className="p-2 border">{s.lateGraceMinutes} min</td>
                  <td className="p-2 border">{s.earlyLeaveGraceMinutes} min</td>
                  {/* <td className="p-2 border">{s.isActive ? "Active" : "Inactive"}</td> */}
                  <td className="p-2 border flex justify-center gap-2">
                    <FaEdit
                      onClick={() => openEditModal(s)}
                      className="cursor-pointer text-blue-600"
                    />
                    <FaTrash
                      onClick={() => handleDelete(s.id)}
                      className="cursor-pointer text-red-600"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MOBILE CARDS */}
      <div className="md:hidden space-y-3 mt-3">
        {filteredShifts.map((s) => (
          <div key={s.id} className="border rounded-xl p-4 shadow-sm bg-white">
            <div className="flex justify-between mb-2">
              <h3 className="font-semibold">{s.shiftName}</h3>
              <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                {s.isActive ? "Active" : "Inactive"}
              </span>
            </div>
            <p><b>Type:</b> {shiftTypeLabel(s.shiftType)}</p>
            <p><b>Reporting:</b> {s.reportingTime}</p>
            <p><b>Closing:</b> {s.closingTime}</p>
            <p><b>Late Grace:</b> {s.lateGraceMinutes} min</p>
            <p><b>Early Leave Grace:</b> {s.earlyLeaveGraceMinutes} min</p>
            <div className="flex justify-end gap-3 mt-2">
              <FaEdit
                onClick={() => openEditModal(s)}
                className="cursor-pointer text-blue-600"
              />
              <FaTrash
                onClick={() => handleDelete(s.id)}
                className="cursor-pointer text-red-600"
              />
            </div>
          </div>
        ))}
      </div>

      {/* ADD/EDIT MODAL */}
      {modalOpen && (
        <AddEditShiftMaster
          editingData={editingData}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

    </div>
  );
};

export default ShiftMaster;